import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { IModalDeleteData, IUser } from '../../../model/user/IUser';
import { UserService } from '../UserService';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { UPDATED_USER } from '../../../enum/Messages';
import { Contact } from '../../../model/shared/Contact';
import { IAgency } from '../../agency/dto/IAgency';
import { CompanyService } from '../../company/CompanyService';
import { Company } from '../../../model/company/Company';
import { formatDate, getCorrectEnums, hasAccessRight } from '../../../utils/Functions';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { useParams } from 'react-router-dom';
import InfluencerRemoveModal from '../../agency/components/InfluencerRemoveModal';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import { ErrorToast, WarningToast } from '../../../utils/toasters';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import MultiSelectDropdown from '../../../shared/components/MultiSelectDropdown';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import ToggleChip from '../../../shared/components/ToggleChip';
import generalSlice from '../../../store/general/GeneralSlice';
import moment from 'moment';
import { UserAdvancedSocialTab } from './UserAdvancedSocialTab';
import { IUserAdvancedInvoiceTabHandle, UserAdvancedInvoiceTab } from './UserAdvancedInvoiceTab';
import { IUserAdvancedDeliveryTabHandle, UserAdvancedDeliveryTab } from './UserAdvancedDeliveryTab';
import { getCountryName } from '../../../utils/helpers/CorrectCountryNames';

const LOCALE_LABELS: Record<string, string> = {
    en: 'English',
    de: 'German',
};
const SUPPORTED_LOCALES = Object.keys(LOCALE_LABELS);
const MIN_INFLUENCER_AGE = 15;
const MAX_DISPLAY_NAME_LENGTH = 30;
const PROFILE_FIELDS = [
    'gender',
    'birthdate',
    'cityOfResidence',
    'about',
    'locale',
    'position',
    'categoryIds',
    'contact',
];
const MIN_CATEGORIES = 3;
const MAX_CATEGORIES = 5;

interface IUserCategory {
    id: number;
    name: string;
    isSelected: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function UserAdvancedEdit() {
    const { id }: any = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const initialContact: Contact = {
        city: '',
        country: '',
        phone: '',
        province: '',
        street: '',
        uidNumber: '',
        zip: '',
    };

    const initialAgency: IAgency = {
        displayName: '',
        firstName: '',
        id: 0,
        lastName: '',
    };

    const [user, setUser] = useState<Partial<IUser>>({
        agency: initialAgency,
        agencyId: 0,
        averageLikeToFollowerRatio: 0,
        birthdate: '',
        brandType: '',
        contact: initialContact,
        displayName: '',
        email: '',
        firstName: '',
        gender: '',
        isEnabled: false,
        lastLogin: '',
        lastLoginIp: '',
        lastName: '',
        referralCode: '',
        roles: [],
        totalEngagements: 0,
        totalFollowers: 0,
        totalReach: 0,
        userType: '',
        username: '',
        hasInfluencerFee: false,
        isIvConnectedClub: false,
        excludeFromMatching: false,
        isPhoneShown: false,
        influencerFeePercentage: 0,
        brandCompany: {
            id: 0,
        },
        hasValueMatch: false,
        valueMatchStart: '',
    });
    const [selectedCompany, setSelectedCompany] = useState('');
    const [companyList, setCompanyList] = useState<Company[]>([]);
    const [filterState, setFilterState] = useState({
        page: 1,
        perPage: 10000,
        title: '',
    });
    const [agencies, setAgencies] = useState<IUser[]>([]);
    const [agenciesParams, setAgenciesParams] = useState('');
    const { userType, roles, gender, country, locale, BrandUserPositionEnum, AgencyUserPositionEnum } = useSelector(
        (state: any) => state.enums
    );
    const [activeTab, setActiveTab] = useState('general');
    const [categories, setCategories] = useState<IUserCategory[]>([]);
    const [statusLoading, setStatusLoading] = useState<string | null>(null);
    const isUserBrand = user?.userType === 'brand_master' || user?.userType === 'brand';
    const isInfluencer = user?.userType === 'influencer';
    const isAgencyUser = user?.userType === 'agency' || user?.userType === 'agency_master';
    const [modalDeleteData, setModalDeleteData] = useState<IModalDeleteData>({
        showModal: false,
        userId: 0,
        userName: '',
    });
    const [change, setChange] = useState(false);
    const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
    const [showErrors, setShowErrors] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [invoiceDirty, setInvoiceDirty] = useState(false);
    const invoiceTabRef = useRef<IUserAdvancedInvoiceTabHandle>(null);
    const [deliveryDirty, setDeliveryDirty] = useState(false);
    const deliveryTabRef = useRef<IUserAdvancedDeliveryTabHandle>(null);

    const isUserDirty = initialSnapshot !== null && JSON.stringify(user) !== initialSnapshot;
    const isDirty = isUserDirty || invoiceDirty || deliveryDirty;

    const changedTabs = useMemo(() => {
        const result = { general: false, profile: false };
        if (initialSnapshot === null) return result;
        const initialUser = JSON.parse(initialSnapshot) as Record<string, unknown>;
        const currentUser = user as Record<string, unknown>;
        const keys = new Set([...Object.keys(initialUser), ...Object.keys(currentUser)]);
        keys.forEach(key => {
            if (JSON.stringify(initialUser[key]) === JSON.stringify(currentUser[key])) return;
            if (PROFILE_FIELDS.includes(key)) {
                result.profile = true;
            } else {
                result.general = true;
            }
        });
        return result;
    }, [user, initialSnapshot]);

    const tabTitle = (title: string, hasChanges: boolean) => (
        <span className="d-inline-flex align-items-center">
            {title}
            {hasChanges && (
                <span
                    className="d-inline-block rounded-circle bg-warning ms-2"
                    style={{ width: 8, height: 8 }}
                    title="Unsaved changes"
                />
            )}
        </span>
    );

    const errors = useMemo(() => {
        const result: Record<string, string> = {};
        if (!user?.email || !EMAIL_REGEX.test(user.email)) result.email = 'Enter a valid email address';
        if (!user?.username?.trim()) result.username = 'Username is required';
        if (!user?.firstName?.trim()) result.firstName = 'First name is required';
        if (!user?.lastName?.trim()) result.lastName = 'Last name is required';
        if (user?.userType === 'influencer' && !user?.displayName?.trim()) {
            result.displayName = 'Display name is required';
        }
        if (user?.userType === 'influencer' && user?.birthdate) {
            const years = moment().diff(moment(user.birthdate), 'years');
            if (new Date(user.birthdate) > new Date()) {
                result.birthdate = 'Birthdate cannot be in the future';
            } else if (years < MIN_INFLUENCER_AGE) {
                result.birthdate = `Minimum age is ${MIN_INFLUENCER_AGE}`;
            }
        }
        if (
            user?.userType === 'influencer' &&
            user?.categoryIds !== undefined &&
            (user.categoryIds.length < MIN_CATEGORIES || user.categoryIds.length > MAX_CATEGORIES)
        ) {
            result.categories = `Select between ${MIN_CATEGORIES} and ${MAX_CATEGORIES} categories`;
        }
        if (user?.userType === 'influencer' && (user?.displayName?.length ?? 0) > MAX_DISPLAY_NAME_LENGTH) {
            result.displayName = `Maximum ${MAX_DISPLAY_NAME_LENGTH} characters`;
        }
        return result;
    }, [
        user?.email,
        user?.username,
        user?.firstName,
        user?.lastName,
        user?.displayName,
        user?.userType,
        user?.birthdate,
        user?.categoryIds,
    ]);

    useEffect(() => {
        UserService.getUser(id)
            .then(async data => {
                let loadedUser = data;
                if (data?.userType === 'influencer') {
                    const categoryList = await UserService.getAdvancedCategories(id);
                    setCategories(categoryList);
                    loadedUser = {
                        ...data,
                        categoryIds: categoryList.filter(category => category.isSelected).map(category => category.id),
                    };
                }
                setUser(loadedUser);
                setInitialSnapshot(JSON.stringify(loadedUser));
                setAgenciesParams(data?.agencyCompany?.displayName ?? '');
                setSelectedCompany(data?.brandCompany?.displayName ?? '');
            })
            .catch(err => ErrorToast(err));
    }, [change]);

    useEffect(() => {
        if (user?.userType === 'influencer' && categories.length === 0 && user?.id) {
            UserService.getAdvancedCategories(id)
                .then(setCategories)
                .catch(err => ErrorToast(err));
        }
    }, [user?.userType, user?.id]);

    useEffect(() => {
        CompanyService.getCompanies(filterState)
            .then(response => {
                setCompanyList(response?.data);
            })
            .catch(err => ErrorToast(err));
    }, [filterState]);

    useEffect(() => {
        UserService.getAgencies({ displayName: agenciesParams, perPage: 10000 })
            .then(data => {
                setAgencies(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [agenciesParams]);

    useEffect(() => {
        if (!isDirty) return;
        const warn = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [isDirty]);

    function confirmAction(title: string, content: string, action: () => void) {
        dispatch(
            generalSlice.actions.setConfirmModal({
                show: true,
                title,
                contentBody: content,
                action: () => {
                    dispatch(generalSlice.actions.setConfirmModal({ show: false }));
                    action();
                },
            })
        );
    }

    function cancelEdit() {
        if (isDirty) {
            confirmAction('Discard changes?', 'You have unsaved changes. Leave without saving?', () => navigate(-1));
        } else {
            navigate(-1);
        }
    }

    function editUser() {
        setShowErrors(true);
        if (Object.keys(errors).length) {
            setActiveTab(
                Object.keys(errors).every(key => ['birthdate', 'categories'].includes(key)) ? 'profile' : 'general'
            );
            WarningToast('Please fix the highlighted fields');
            return;
        }
        if (invoiceDirty && !invoiceTabRef.current?.validate()) {
            setActiveTab('invoice');
            WarningToast('Please fix the highlighted fields');
            return;
        }
        if (deliveryDirty && !deliveryTabRef.current?.validate()) {
            setActiveTab('delivery');
            WarningToast('Please fix the highlighted fields');
            return;
        }
        setIsSaving(true);
        // Invoice and delivery data have their own endpoints, so they are saved first and the user is only left once everything succeeds.
        const saveInvoice = invoiceDirty ? invoiceTabRef.current?.save() ?? Promise.resolve() : Promise.resolve();
        saveInvoice
            .then(() => (deliveryDirty ? deliveryTabRef.current?.save() : undefined))
            .then(() => (isUserDirty ? UserService.editUserAdvanced(id, user) : null))
            .then((data: IUser | null) => {
                if (data) {
                    setUser(data);
                    setInitialSnapshot(JSON.stringify(data));
                }
                toast(UPDATED_USER);
                navigate(-1);
            })
            .catch((err: unknown) => ErrorToast(err))
            .finally(() => setIsSaving(false));
    }

    // function removeUserFromAgency() {
    //     UserService.editUser(id, user)
    //         .then(data => {
    //             setUser(data);
    //             toast(UPDATED_USER);
    //             navigate(-1);
    //         })
    //         .catch(err => ErrorToast(err));
    // }

    function handleChange(evt: any, isNumber = false) {
        const { name } = evt.target;
        const value = isNumber ? +evt.target.value : evt.target.value;
        setUser(prev => ({
            ...prev,
            [name]: value,
        }));
    }

    function toggleStatus(
        field: 'isEnabled' | 'isSuspended' | 'isOnboarded',
        request: (userId: number) => Promise<unknown>,
        successMessage: string
    ) {
        const next = !user?.[field];
        setStatusLoading(field);
        request(Number(id))
            .then(() => {
                setUser(prev => ({ ...prev, [field]: next }));
                setInitialSnapshot(prev => (prev ? JSON.stringify({ ...JSON.parse(prev), [field]: next }) : prev));
                toast(successMessage);
            })
            .catch(err => ErrorToast(err))
            .finally(() => setStatusLoading(null));
    }

    function toggleCategory(categoryId: number) {
        const current = user?.categoryIds ?? categories.filter(category => category.isSelected).map(c => c.id);
        if (current.includes(categoryId)) {
            setUser(prev => ({ ...prev, categoryIds: current.filter(idItem => idItem !== categoryId) }));
            return;
        }
        if (current.length >= MAX_CATEGORIES) {
            WarningToast(`You can select up to ${MAX_CATEGORIES} categories`);
            return;
        }
        setUser(prev => ({ ...prev, categoryIds: [...current, categoryId] }));
    }

    function handleContactChange(name: string, value: string) {
        setUser(prev => ({ ...prev, contact: { ...(prev.contact ?? initialContact), [name]: value } }));
    }

    function handleCheckbox(evt: React.ChangeEvent<HTMLInputElement>) {
        const { name, checked } = evt.target;

        setUser(prev => {
            if (!prev) return prev;

            if (name === 'hasInfluencerFee' && !checked) {
                return {
                    ...prev,
                    hasInfluencerFee: false,
                    isIvConnectedClub: false,
                };
            }

            return {
                ...prev,
                [name]: checked,
            };
        });
    }

    const changeFilterHandler = (value: string) => {
        setSelectedCompany(value);
        setFilterState(prev => ({ ...prev, displayName: value }));
    };

    const changeAutoCompleteHandler = (valueToUpdate: Company, field: string) => {
        if (field === 'brandCompany') {
            setSelectedCompany(valueToUpdate?.displayName);
        } else {
            setAgenciesParams(valueToUpdate?.displayName);
            // setUser({...user, agency: {id: valueToUpdate?.id}, agencyCompany: {displayName: valueToUpdate?.displayName}})
        }

        setUser(prev => ({ ...prev, [field]: field === 'agency' ? valueToUpdate.id : valueToUpdate }));
    };
    const userTitle = user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    const headerDetails = [
        `ID ${user?.id ?? id}`,
        user?.lastLogin ? `Last login ${formatDate(user.lastLogin)}` : null,
        user?.lastLoginIp ? `IP ${user.lastLoginIp}` : null,
    ].filter(Boolean);

    const sectionTitle = (title: string) => <h5 className="mb-4">{title}</h5>;

    const statusRow = (
        label: string,
        description: string,
        isPositive: boolean,
        positiveLabel: string,
        negativeLabel: string,
        buttonLabel: string,
        buttonVariant: string,
        field: string,
        onClick: () => void
    ) => (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-3 border-bottom">
            <div>
                <div className="d-flex align-items-center gap-3">
                    <span className="fw-bold">{label}</span>
                    <Badge
                        bg=""
                        className={`fs-8 ${
                            isPositive ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'
                        }`}
                    >
                        {isPositive ? positiveLabel : negativeLabel}
                    </Badge>
                </div>
                <div className="text-muted fs-7 mt-1">{description}</div>
            </div>
            <Button variant={buttonVariant} size="sm" onClick={onClick} disabled={statusLoading !== null}>
                {statusLoading === field ? 'Saving...' : buttonLabel}
            </Button>
        </div>
    );

    return (
        <>
            <InfluencerRemoveModal {...modalDeleteData} setModalDeleteData={setModalDeleteData} setChange={setChange} />

            <Card className="mb-4">
                <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-4">
                        <img
                            src={
                                user?.profilePhoto?.thumbnail ||
                                user?.profilePhoto?.path ||
                                '/icons/UserPlaceholder.png'
                            }
                            onError={event => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = '/icons/UserPlaceholder.png';
                            }}
                            alt={userTitle || 'User'}
                            width={72}
                            height={72}
                            className="rounded flex-shrink-0"
                            style={{ objectFit: 'cover' }}
                        />
                        <div>
                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <h3 className="m-0">{userTitle || 'User'}</h3>
                                {user?.userType && (
                                    <Badge bg="" className="bg-primary-subtle text-primary fs-8">
                                        {getCorrectEnums(user.userType)}
                                    </Badge>
                                )}
                                <Badge
                                    bg=""
                                    className={`fs-8 ${
                                        user?.isEnabled
                                            ? 'bg-success-subtle text-success'
                                            : 'bg-danger-subtle text-danger'
                                    }`}
                                >
                                    {user?.isEnabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                                {user?.isSuspended && (
                                    <Badge bg="" className="bg-warning-subtle text-warning fs-8">
                                        Suspended
                                    </Badge>
                                )}
                                {user?.isOnboarded && (
                                    <Badge bg="" className="bg-info-subtle text-info fs-8">
                                        Onboarded
                                    </Badge>
                                )}
                            </div>
                            <div className="text-muted fs-7 mt-2">{headerDetails.join(' · ')}</div>
                        </div>
                    </div>
                    <ImpersonateEye id={id} isEnabled={user.isEnabled} classes="btn-primary btn-icon" />
                </Card.Body>
            </Card>

            <Form onSubmit={event => event.preventDefault()}>
                <Card className="mb-4 tabs-flat-cards">
                    <Card.Body>
                        <Tabs
                            id="user-advanced-edit-tabs"
                            activeKey={activeTab}
                            onSelect={key => setActiveTab(key ?? 'general')}
                            className="mb-4 tabs-primary-border"
                        >
                            <Tab eventKey="general" title={tabTitle('General', changedTabs.general)}>
                                <Row className="g-4 mb-4">
                                    <Col xl={6}>
                                        <Card className="h-100">
                                            <Card.Body>
                                                {sectionTitle('Account')}
                                                <Row>
                                                    <Form.Group
                                                        as={Col}
                                                        md={6}
                                                        controlId="formGridEmail"
                                                        className="mb-3"
                                                    >
                                                        <Form.Label>Email</Form.Label>
                                                        <Form.Control
                                                            name="email"
                                                            onChange={handleChange}
                                                            value={user?.email ?? ''}
                                                            type="email"
                                                            isInvalid={showErrors && !!errors.email}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.email}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                    <Form.Group
                                                        as={Col}
                                                        md={6}
                                                        controlId="formGridUsername"
                                                        className="mb-3"
                                                    >
                                                        <Form.Label>Username</Form.Label>
                                                        <Form.Control
                                                            name="username"
                                                            onChange={handleChange}
                                                            value={user?.username ?? ''}
                                                            isInvalid={showErrors && !!errors.username}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.username}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Row>
                                                <Form.Group controlId="formGridDisplayName" className="mb-3">
                                                    <Form.Label>Display name</Form.Label>
                                                    <Form.Control
                                                        name="displayName"
                                                        onChange={handleChange}
                                                        value={user?.displayName ?? ''}
                                                        disabled={user.userType != 'influencer'}
                                                        isInvalid={showErrors && !!errors.displayName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.displayName}
                                                    </Form.Control.Feedback>
                                                    {user.userType != 'influencer' && (
                                                        <Form.Text className="text-muted">
                                                            Generated automatically from first and last name.
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                                <Row>
                                                    <Form.Group
                                                        as={Col}
                                                        md={6}
                                                        controlId="formFirstName"
                                                        className="mb-3"
                                                    >
                                                        <Form.Label>First name</Form.Label>
                                                        <Form.Control
                                                            name="firstName"
                                                            onChange={handleChange}
                                                            value={user?.firstName ?? ''}
                                                            isInvalid={showErrors && !!errors.firstName}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.firstName}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                    <Form.Group
                                                        as={Col}
                                                        md={6}
                                                        controlId="formLastName"
                                                        className="mb-3"
                                                    >
                                                        <Form.Label>Last name</Form.Label>
                                                        <Form.Control
                                                            name="lastName"
                                                            onChange={handleChange}
                                                            value={user?.lastName ?? ''}
                                                            isInvalid={showErrors && !!errors.lastName}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.lastName}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col xl={6}>
                                        <Card className="h-100">
                                            <Card.Body>
                                                {sectionTitle('Access')}
                                                <Form.Group controlId="formGridType" className="mb-3">
                                                    <Form.Label>Type</Form.Label>
                                                    <SingleSelectDropdown
                                                        placeholder="Select type"
                                                        clearable={false}
                                                        options={userType.map((type: string) => ({
                                                            value: type,
                                                            label: getCorrectEnums(type),
                                                        }))}
                                                        selected={user?.userType ?? ''}
                                                        onChange={value => {
                                                            if (!value || value === user?.userType) return;
                                                            if (!user?.userType) {
                                                                setUser(prev => ({ ...prev, userType: value }));
                                                                return;
                                                            }
                                                            confirmAction(
                                                                'Change user type?',
                                                                `Changing the type to "${getCorrectEnums(
                                                                    value
                                                                )}" changes what this user can see and do. Continue?`,
                                                                () => setUser(prev => ({ ...prev, userType: value }))
                                                            );
                                                        }}
                                                    />
                                                </Form.Group>
                                                <Form.Group controlId="formGridRoles" className="mb-3">
                                                    <Form.Label>Roles</Form.Label>
                                                    <MultiSelectDropdown
                                                        placeholder="Select roles"
                                                        options={roles.map((role: string) => ({
                                                            value: role,
                                                            label: role,
                                                        }))}
                                                        selected={user.roles ?? []}
                                                        onChange={selectedRoles =>
                                                            setUser(prev => ({ ...prev, roles: selectedRoles }))
                                                        }
                                                        disabled={!hasAccessRight(AdminAclEnum.CHANGE_USER_ROLES)}
                                                    />
                                                </Form.Group>
                                                {isUserBrand && (
                                                    <Form.Group controlId="formGridCompany" className="mb-3">
                                                        <div className="autocomplete-input position-relative d-flex flex-column">
                                                            <Form.Label>Company</Form.Label>
                                                            <AutocompleteInput
                                                                items={companyList}
                                                                renderItems={['displayName']}
                                                                value={selectedCompany ?? ''}
                                                                setFilter={value => changeFilterHandler(value)}
                                                                onSelect={company =>
                                                                    changeAutoCompleteHandler(company, 'brandCompany')
                                                                }
                                                            />
                                                        </div>
                                                    </Form.Group>
                                                )}
                                                {isInfluencer && (
                                                    <>
                                                        <Form.Group
                                                            controlId="formGridAgency"
                                                            className="autocomplete-input zindex-5 position-relative d-flex flex-column mb-3"
                                                        >
                                                            <Form.Label>Agency</Form.Label>
                                                            <AutocompleteInput
                                                                items={agencies}
                                                                value={agenciesParams ?? ''}
                                                                renderItems={['displayName']}
                                                                setFilter={(value: string) => setAgenciesParams(value)}
                                                                onSelect={agency =>
                                                                    changeAutoCompleteHandler(agency, 'agency')
                                                                }
                                                            />
                                                        </Form.Group>
                                                        {agenciesParams && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() =>
                                                                    setModalDeleteData({
                                                                        ...modalDeleteData,
                                                                        showModal: true,
                                                                        userId: Number(user.id),
                                                                        userName: user?.displayName,
                                                                    })
                                                                }
                                                            >
                                                                Remove from agency
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                                {isAgencyUser && (
                                                    <Form.Group controlId="formGridAgencyReadOnly" className="mb-3">
                                                        <Form.Label>Agency</Form.Label>
                                                        <Form.Control
                                                            value={agenciesParams ?? ''}
                                                            placeholder="-"
                                                            readOnly
                                                        />
                                                        <Form.Text className="text-muted">
                                                            Agency users are tied to their agency and cannot be
                                                            reassigned here.
                                                        </Form.Text>
                                                    </Form.Group>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                <Card className="mb-4">
                                    <Card.Body>
                                        {sectionTitle('Settings')}
                                        <div className="text-muted fs-8 mb-2">Permissions</div>
                                        <div className="d-flex flex-wrap gap-3 mb-4">
                                            <ToggleChip
                                                name="isSupervisor"
                                                label="Supervisor"
                                                checked={!!user?.isSupervisor}
                                                onChange={handleCheckbox}
                                            />
                                        </div>

                                        {isInfluencer && (
                                            <>
                                                <div className="text-muted fs-8 mb-2">Influencer</div>
                                                <div
                                                    className="d-flex flex-wrap align-items-center gap-3 mb-4"
                                                    key={user?.id}
                                                >
                                                    <ToggleChip
                                                        name="hasValueMatch"
                                                        label="Value match"
                                                        checked={!!user?.hasValueMatch}
                                                        onChange={handleCheckbox}
                                                    />
                                                    <ToggleChip
                                                        name="hasInfluencerFee"
                                                        label="Influencer fee"
                                                        checked={!!user?.hasInfluencerFee}
                                                        disabled={!hasAccessRight(AdminAclEnum.INFLUENCER_FEE)}
                                                        onChange={handleCheckbox}
                                                    />
                                                    <ToggleChip
                                                        name="isIvConnectedClub"
                                                        label="IV Connected Club"
                                                        checked={!!user?.isIvConnectedClub}
                                                        disabled={
                                                            !hasAccessRight(AdminAclEnum.INFLUENCER_FEE) ||
                                                            !user?.hasInfluencerFee
                                                        }
                                                        onChange={handleCheckbox}
                                                    />
                                                    <ToggleChip
                                                        name="excludeFromMatching"
                                                        label="Exclude from matching"
                                                        checked={!!user?.excludeFromMatching}
                                                        onChange={handleCheckbox}
                                                    />
                                                </div>
                                                {user?.hasValueMatch && (
                                                    <Form.Group
                                                        controlId="formGridValueMatchStart"
                                                        className="mb-4"
                                                        style={{ maxWidth: 240 }}
                                                    >
                                                        <Form.Label>Value match start</Form.Label>
                                                        <Form.Control
                                                            name="valueMatchStart"
                                                            onChange={handleChange}
                                                            value={
                                                                user?.valueMatchStart
                                                                    ? DATE_ONLY_REGEX.test(user.valueMatchStart)
                                                                        ? user.valueMatchStart
                                                                        : new Date(
                                                                              user.valueMatchStart
                                                                          ).toLocaleDateString('en-CA')
                                                                    : ''
                                                            }
                                                            type="date"
                                                        />
                                                    </Form.Group>
                                                )}
                                            </>
                                        )}

                                        {isUserBrand && (
                                            <>
                                                <div className="text-muted fs-8 mb-2">Brand</div>
                                                <div className="d-flex flex-wrap gap-3">
                                                    <ToggleChip
                                                        name="isPhoneShown"
                                                        label="Show mobile"
                                                        checked={!!user?.isPhoneShown}
                                                        onChange={handleCheckbox}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab eventKey="profile" title={tabTitle('Profile', changedTabs.profile)}>
                                <Row className="g-4 mb-4">
                                    <Col xl={isInfluencer ? 6 : 12}>
                                        <Card className="h-100">
                                            <Card.Body>
                                                {sectionTitle('Personal')}
                                                {isInfluencer && (
                                                    <>
                                                        <Row>
                                                            <Form.Group as={Col} md={6} className="mb-3">
                                                                <Form.Label>Gender</Form.Label>
                                                                <SingleSelectDropdown
                                                                    placeholder="Select gender"
                                                                    options={(gender ?? [])
                                                                        .filter(
                                                                            (value: string) => value !== 'undefined'
                                                                        )
                                                                        .map((value: string) => ({
                                                                            value,
                                                                            label: getCorrectEnums(value),
                                                                        }))}
                                                                    selected={user?.gender ?? ''}
                                                                    onChange={value =>
                                                                        setUser(prev => ({ ...prev, gender: value }))
                                                                    }
                                                                />
                                                            </Form.Group>
                                                            <Form.Group
                                                                as={Col}
                                                                md={6}
                                                                controlId="formBirthdate"
                                                                className="mb-3"
                                                            >
                                                                <Form.Label>Birthdate</Form.Label>
                                                                <Form.Control
                                                                    type="date"
                                                                    name="birthdate"
                                                                    value={
                                                                        user?.birthdate
                                                                            ? String(user.birthdate).slice(0, 10)
                                                                            : ''
                                                                    }
                                                                    onChange={handleChange}
                                                                    isInvalid={!!errors.birthdate}
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.birthdate}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>
                                                        </Row>
                                                        <Row>
                                                            <Form.Group
                                                                as={Col}
                                                                md={6}
                                                                controlId="formCity"
                                                                className="mb-3"
                                                            >
                                                                <Form.Label>City of residence</Form.Label>
                                                                <Form.Control
                                                                    name="cityOfResidence"
                                                                    value={user?.cityOfResidence ?? ''}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Group>
                                                            <Form.Group as={Col} md={6} className="mb-3">
                                                                <Form.Label>Country</Form.Label>
                                                                <SingleSelectDropdown
                                                                    placeholder="Select country"
                                                                    withImage
                                                                    options={(country ?? []).map((code: string) => ({
                                                                        value: code,
                                                                        label: getCountryName(code),
                                                                        image: `/flags/4x3/${code.toLowerCase()}.svg`,
                                                                    }))}
                                                                    selected={user?.contact?.country ?? ''}
                                                                    onChange={value =>
                                                                        handleContactChange('country', value)
                                                                    }
                                                                />
                                                            </Form.Group>
                                                        </Row>
                                                    </>
                                                )}
                                                <Row>
                                                    <Form.Group
                                                        as={Col}
                                                        md={isInfluencer ? 6 : 4}
                                                        controlId="formPhone"
                                                        className="mb-3"
                                                    >
                                                        <Form.Label>Phone number</Form.Label>
                                                        <Form.Control
                                                            name="phone"
                                                            value={user?.contact?.phone ?? ''}
                                                            onChange={event =>
                                                                handleContactChange('phone', event.target.value)
                                                            }
                                                        />
                                                    </Form.Group>
                                                    {(isUserBrand || isAgencyUser) && (
                                                        <Form.Group as={Col} md={4} className="mb-3">
                                                            <Form.Label>Position</Form.Label>
                                                            <SingleSelectDropdown
                                                                placeholder="Select position"
                                                                options={(
                                                                    (isUserBrand
                                                                        ? BrandUserPositionEnum
                                                                        : AgencyUserPositionEnum) ?? []
                                                                ).map((value: string) => ({
                                                                    value,
                                                                    label: getCorrectEnums(value),
                                                                }))}
                                                                selected={user?.position ?? ''}
                                                                onChange={value =>
                                                                    setUser(prev => ({ ...prev, position: value }))
                                                                }
                                                            />
                                                        </Form.Group>
                                                    )}
                                                    <Form.Group as={Col} md={isInfluencer ? 6 : 4} className="mb-3">
                                                        <Form.Label>Language</Form.Label>
                                                        <SingleSelectDropdown
                                                            placeholder="Select language"
                                                            options={(locale ?? [])
                                                                .filter((value: string) =>
                                                                    SUPPORTED_LOCALES.includes(value)
                                                                )
                                                                .map((value: string) => ({
                                                                    value,
                                                                    label:
                                                                        LOCALE_LABELS[value] ?? getCorrectEnums(value),
                                                                }))}
                                                            selected={user?.locale ?? ''}
                                                            onChange={value =>
                                                                setUser(prev => ({ ...prev, locale: value }))
                                                            }
                                                        />
                                                    </Form.Group>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    {isInfluencer && (
                                        <Col xl={6}>
                                            <Card className="h-100">
                                                <Card.Body>
                                                    {sectionTitle('About')}
                                                    <Form.Group controlId="formAbout">
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={10}
                                                            name="about"
                                                            value={user?.about ?? ''}
                                                            onChange={handleChange}
                                                            placeholder="Profile description"
                                                        />
                                                    </Form.Group>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    )}
                                </Row>
                                {isInfluencer && (
                                    <Card className="mb-4">
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-baseline mb-1">
                                                {sectionTitle('Categories')}
                                                <span
                                                    className={`fs-7 ${
                                                        errors.categories ? 'text-danger' : 'text-muted'
                                                    }`}
                                                >
                                                    {(user?.categoryIds ?? []).length} / {MAX_CATEGORIES} selected
                                                </span>
                                            </div>
                                            <div className="text-muted fs-7 mb-3">
                                                Choose between {MIN_CATEGORIES} and {MAX_CATEGORIES} categories.
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {categories.map(category => {
                                                    const isSelected = (user?.categoryIds ?? []).includes(category.id);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={category.id}
                                                            onClick={() => toggleCategory(category.id)}
                                                            className={`d-inline-flex align-items-center gap-2 rounded-pill border px-3 py-2 bg-white fs-7 ${
                                                                isSelected
                                                                    ? 'border-primary bg-primary-subtle'
                                                                    : 'border-secondary-subtle'
                                                            }`}
                                                        >
                                                            <img
                                                                src={`/icons/categories/${category.name}.svg`}
                                                                alt=""
                                                                width={20}
                                                            />
                                                            {getCorrectEnums(category.name)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {errors.categories && (
                                                <div className="text-danger fs-8 mt-3">{errors.categories}</div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                )}
                            </Tab>

                            {isInfluencer && (
                                <Tab eventKey="social" title="Social media">
                                    <UserAdvancedSocialTab userId={Number(id)} isActive={activeTab === 'social'} />
                                </Tab>
                            )}

                            {isInfluencer && (
                                <Tab eventKey="invoice" title={tabTitle('Invoice data', invoiceDirty)}>
                                    <UserAdvancedInvoiceTab
                                        ref={invoiceTabRef}
                                        userId={Number(id)}
                                        isActive={activeTab === 'invoice'}
                                        onDirtyChange={setInvoiceDirty}
                                    />
                                </Tab>
                            )}

                            {isInfluencer && (
                                <Tab eventKey="delivery" title={tabTitle('Delivery data', deliveryDirty)}>
                                    <UserAdvancedDeliveryTab
                                        ref={deliveryTabRef}
                                        userId={Number(id)}
                                        isActive={activeTab === 'delivery'}
                                        onDirtyChange={setDeliveryDirty}
                                    />
                                </Tab>
                            )}

                            <Tab eventKey="status" title="Status">
                                <Card className="mb-4">
                                    <Card.Body>
                                        {sectionTitle('Account status')}
                                        {statusRow(
                                            'Account',
                                            'Disabled users cannot log in.',
                                            !!user?.isEnabled,
                                            'Enabled',
                                            'Disabled',
                                            user?.isEnabled ? 'Disable account' : 'Enable account',
                                            user?.isEnabled ? 'outline-danger' : 'outline-primary',
                                            'isEnabled',
                                            () =>
                                                confirmAction(
                                                    user?.isEnabled ? 'Disable account?' : 'Enable account?',
                                                    user?.isEnabled
                                                        ? 'This user will no longer be able to log in.'
                                                        : 'This user will be able to log in again.',
                                                    () =>
                                                        toggleStatus(
                                                            'isEnabled',
                                                            UserService.disableUser,
                                                            user?.isEnabled ? 'Account disabled' : 'Account enabled'
                                                        )
                                                )
                                        )}
                                        {statusRow(
                                            'Suspension',
                                            'Suspended users see a suspension message when they log in and cannot do anything until they contact support.',
                                            !user?.isSuspended,
                                            'Active',
                                            'Suspended',
                                            user?.isSuspended ? 'Remove suspension' : 'Suspend user',
                                            user?.isSuspended ? 'outline-primary' : 'outline-danger',
                                            'isSuspended',
                                            () =>
                                                confirmAction(
                                                    user?.isSuspended ? 'Remove suspension?' : 'Suspend user?',
                                                    user?.isSuspended
                                                        ? 'This user will be able to use the app again.'
                                                        : 'This user will see a suspension message when they log in and cannot do anything until they contact support.',
                                                    () =>
                                                        toggleStatus(
                                                            'isSuspended',
                                                            UserService.suspendUser,
                                                            user?.isSuspended ? 'Suspension removed' : 'User suspended'
                                                        )
                                                )
                                        )}
                                        {isInfluencer &&
                                            statusRow(
                                                'Onboarding',
                                                'Marks the influencer as onboarded.',
                                                !!user?.isOnboarded,
                                                'Onboarded',
                                                'Not onboarded',
                                                user?.isOnboarded ? 'Mark as not onboarded' : 'Mark as onboarded',
                                                'outline-primary',
                                                'isOnboarded',
                                                () =>
                                                    confirmAction(
                                                        user?.isOnboarded
                                                            ? 'Mark as not onboarded?'
                                                            : 'Mark as onboarded?',
                                                        user?.isOnboarded
                                                            ? 'The influencer will be marked as not onboarded.'
                                                            : 'The influencer will be marked as onboarded.',
                                                        () =>
                                                            toggleStatus(
                                                                'isOnboarded',
                                                                UserService.onboardUser,
                                                                user?.isOnboarded
                                                                    ? 'Marked as not onboarded'
                                                                    : 'Marked as onboarded'
                                                            )
                                                    )
                                            )}
                                    </Card.Body>
                                </Card>

                                <Card className="mb-4">
                                    <Card.Body>
                                        {sectionTitle('Registration')}
                                        <Row>
                                            <Col md={4} className="mb-3">
                                                <div className="fs-8 text-muted">Registration complete</div>
                                                <div>{user?.isRegistrationComplete ? 'Yes' : 'No'}</div>
                                            </Col>
                                            <Col md={4} className="mb-3">
                                                <div className="fs-8 text-muted">Registration step</div>
                                                <div>{user?.registrationStep ?? '-'}</div>
                                            </Col>
                                            <Col md={4} className="mb-3">
                                                <div className="fs-8 text-muted">Banner seen</div>
                                                <div>{user?.hasSeenBanner ? 'Yes' : 'No'}</div>
                                            </Col>
                                            {user?.verificationUUID && (
                                                <Col xs={12} className="mb-3">
                                                    <div className="fs-8 text-muted">Verification UUID</div>
                                                    <div className="text-break">{user.verificationUUID}</div>
                                                </Col>
                                            )}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Tab>
                        </Tabs>
                    </Card.Body>
                </Card>

                <div
                    className="position-sticky bottom-0 bg-white border-top py-3 px-4 mb-5 d-flex align-items-center justify-content-between rounded shadow-sm"
                    style={{ zIndex: 10 }}
                >
                    <span className={`fs-7 ${isDirty ? 'text-warning' : 'text-muted'}`}>
                        {isDirty ? '● Unsaved changes' : 'No changes'}
                    </span>
                    <div className="d-flex gap-2">
                        <Button variant="outline-primary" onClick={cancelEdit} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button onClick={() => editUser()} disabled={!isDirty || isSaving}>
                            Save
                        </Button>
                    </div>
                </div>
            </Form>
        </>
    );
}
